import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Drawer,
  Dropdown,
  Input,
  Modal,
  Select,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { PAYMENT_STATUS_LABELS, TRANSACTION_TYPE_LABELS } from '../../../../services/purchaseDealSheetService';
import { FilePdfOutlined, FilterOutlined, MoreOutlined, PlusOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import { FiRefreshCw } from 'react-icons/fi';
import DashboardPageWrapper from '../../../../components/layout/DashboardPageWrapper';
import PermissionGate from '../../../../components/auth/PermissionGate';
import { useAuth } from '../../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../../config/permissions';
import purchaseDealSheetService from '../../../../services/purchaseDealSheetService';
import { getPurchaseDealSheetColumns } from './purchaseDealSheetColumns';
import SupplierDealSheetUploadModal from './SupplierDealSheetUploadModal';
import {
  PAYMENT_STATUS_FILTER_OPTIONS,
  SUPPLIER_SEARCH_MIN,
  TRANSACTION_TYPE_FILTER_OPTIONS,
} from './purchaseDealSheet.constants';
import {
  apiErrMsg,
  canDeleteDealSheet,
  resolveDealSheetId,
  toNumber,
} from './purchaseDealSheet.helpers';

const DEFAULT_SORT = { sort_by: 'id', sort_order: 'desc' };

function FilterField({ label, children }) {
  return (
    <div className="min-w-0">
      <label className="mb-1.5 block text-sm font-medium text-gray-700">{label}</label>
      {children}
    </div>
  );
}

export default function PurchaseDealSheetListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(PERMISSIONS.PRODUCT_PRE_IPO_PURCHASE_DEAL_SHEET_MANAGE);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadModalRow, setUploadModalRow] = useState(null);

  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') || '');
  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const [paymentStatus, setPaymentStatus] = useState(
    () => searchParams.get('payment_status') || '',
  );
  const [transactionType, setTransactionType] = useState(
    () => searchParams.get('transaction_type') || '',
  );
  const [pagination, setPagination] = useState(() => ({
    current: Math.max(1, Number(searchParams.get('page')) || 1),
    pageSize: Math.max(1, Number(searchParams.get('limit')) || 10),
    total: 0,
  }));
  const [sortState, setSortState] = useState(() => ({
    sort_by: searchParams.get('sort_by') || DEFAULT_SORT.sort_by,
    sort_order: searchParams.get('sort_order') || DEFAULT_SORT.sort_order,
  }));

  const fetchList = useCallback(
    async ({
      page = pagination.current,
      pageSize = pagination.pageSize,
      currentSearch = search,
      currentPaymentStatus = paymentStatus,
      currentTransactionType = transactionType,
      sort = sortState,
    } = {}) => {
      setLoading(true);
      setError('');
      try {
        const params = {
          page,
          limit: pageSize,
          sort_by: sort.sort_by,
          sort_order: sort.sort_order,
        };
        if (currentSearch?.trim().length >= SUPPLIER_SEARCH_MIN) {
          params.search = currentSearch.trim();
        }
        if (currentPaymentStatus) params.payment_status = currentPaymentStatus;
        if (currentTransactionType) params.transaction_type = currentTransactionType;

        const data = await purchaseDealSheetService.listDealSheets(params);
        const items = Array.isArray(data?.items) ? data.items : [];
        const pag = data?.pagination || {};

        setRows(items);
        setPagination({
          current: toNumber(pag.page) || page,
          pageSize: toNumber(pag.limit) || pageSize,
          total: toNumber(pag.total) || items.length,
        });
      } catch (e) {
        setRows([]);
        setError(apiErrMsg(e));
      } finally {
        setLoading(false);
      }
    },
    [pagination.current, pagination.pageSize, search, paymentStatus, transactionType, sortState],
  );

  const fetchListRef = useRef(fetchList);
  useEffect(() => {
    fetchListRef.current = fetchList;
  }, [fetchList]);

  useEffect(() => {
    fetchListRef.current();
  }, [search, paymentStatus, transactionType, sortState.sort_by, sortState.sort_order]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPagination((prev) => ({ ...prev, current: 1 }));
        fetchListRef.current({ page: 1, currentSearch: searchInput });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, search]);

  useEffect(() => {
    const p = {};
    if (search) p.q = search;
    if (paymentStatus) p.payment_status = paymentStatus;
    if (transactionType) p.transaction_type = transactionType;
    if (pagination.current > 1) p.page = String(pagination.current);
    if (pagination.pageSize !== 10) p.limit = String(pagination.pageSize);
    if (sortState.sort_by !== DEFAULT_SORT.sort_by) p.sort_by = sortState.sort_by;
    if (sortState.sort_order !== DEFAULT_SORT.sort_order) p.sort_order = sortState.sort_order;
    setSearchParams(p, { replace: true });
  }, [search, paymentStatus, transactionType, pagination.current, pagination.pageSize, sortState, setSearchParams]);

  const filtersRef = useRef({ search, paymentStatus, transactionType });
  const sortStateRef = useRef(sortState);
  const paginationRef = useRef(pagination);
  useEffect(() => {
    filtersRef.current = { search, paymentStatus, transactionType };
  }, [search, paymentStatus, transactionType]);
  useEffect(() => {
    sortStateRef.current = sortState;
  }, [sortState]);
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const handleTableChange = useCallback((newPagination, _, sorter) => {
    const currentSort = sortStateRef.current;
    const currentPagination = paginationRef.current;
    const currentFilters = filtersRef.current;

    const newSort =
      sorter?.field && sorter?.order
        ? {
            sort_by: sorter.field,
            sort_order: sorter.order === 'ascend' ? 'asc' : 'desc',
          }
        : currentSort;

    const pageChanged =
      newPagination.current !== currentPagination.current ||
      newPagination.pageSize !== currentPagination.pageSize;
    const sortChanged =
      newSort.sort_by !== currentSort.sort_by || newSort.sort_order !== currentSort.sort_order;

    if (!pageChanged && !sortChanged) return;

    if (sortChanged) setSortState(newSort);
    fetchListRef.current({
      page: sortChanged ? 1 : newPagination.current,
      pageSize: newPagination.pageSize,
      currentSearch: currentFilters.search,
      currentPaymentStatus: currentFilters.paymentStatus,
      currentTransactionType: currentFilters.transactionType,
      sort: newSort,
    });
  }, []);

  const handleView = useCallback(
    (row) => {
      const id = resolveDealSheetId(row);
      if (id) navigate(`/pre-ipo/purchase-deal-sheet/${encodeURIComponent(String(id))}`);
    },
    [navigate],
  );

  const handleDelete = useCallback(async (row) => {
    try {
      const id = resolveDealSheetId(row);
      await purchaseDealSheetService.deleteDealSheet(id);
      message.success('Purchase deal sheet deleted');
      fetchListRef.current();
    } catch (e) {
      message.error(apiErrMsg(e));
    }
  }, []);

  const handleGeneratePdf = useCallback(async (row, e) => {
    e?.stopPropagation?.();
    const id = resolveDealSheetId(row);
    if (!id) return;

    const runGenerate = async (forceRegenerate) => {
      setGeneratingId(id);
      try {
        const result = await purchaseDealSheetService.generateDealSheetPdf(id, {
          force_regenerate: forceRegenerate,
        });
        message.success(forceRegenerate ? 'Deal sheet PDF regenerated' : 'Deal sheet PDF generated');
        if (result?.url) window.open(result.url, '_blank', 'noopener,noreferrer');
        fetchListRef.current();
      } catch (err) {
        message.error(apiErrMsg(err));
      } finally {
        setGeneratingId(null);
      }
    };

    if (row.pdf_url) {
      Modal.confirm({
        title: 'Regenerate deal sheet PDF?',
        content: 'This will overwrite the existing PDF with current deal sheet data.',
        okText: 'Regenerate',
        onOk: () => runGenerate(true),
      });
      return;
    }

    await runGenerate(false);
  }, []);

  const handleOpenUploadModal = useCallback((row, e) => {
    e?.stopPropagation?.();
    setUploadModalRow(row);
    setUploadModalOpen(true);
  }, []);

  const handleUploadSuccess = useCallback((result) => {
    const dealSheetId = resolveDealSheetId(uploadModalRow);
    const nextUrl = result?.supplier_deal_sheet_url ?? result?.url ?? result;

    if (dealSheetId && typeof nextUrl === 'string') {
      setRows((prev) =>
        prev.map((row) =>
          resolveDealSheetId(row) === dealSheetId
            ? { ...row, supplier_deal_sheet_url: nextUrl }
            : row,
        ),
      );
      setUploadModalRow((prev) =>
        prev && resolveDealSheetId(prev) === dealSheetId
          ? { ...prev, supplier_deal_sheet_url: nextUrl }
          : prev,
      );
      return;
    }

    fetchListRef.current();
  }, [uploadModalRow]);

  const handleViewRef = useRef(handleView);
  const handleDeleteRef = useRef(handleDelete);
  const handleGeneratePdfRef = useRef(handleGeneratePdf);
  const handleOpenUploadModalRef = useRef(handleOpenUploadModal);
  useEffect(() => {
    handleViewRef.current = handleView;
  }, [handleView]);
  useEffect(() => {
    handleDeleteRef.current = handleDelete;
  }, [handleDelete]);
  useEffect(() => {
    handleGeneratePdfRef.current = handleGeneratePdf;
  }, [handleGeneratePdf]);
  useEffect(() => {
    handleOpenUploadModalRef.current = handleOpenUploadModal;
  }, [handleOpenUploadModal]);

  const columns = useMemo(() => {
    const actionsColumn = {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 140,
      render: (_, row) => {
        const items = [
          {
            key: 'view',
            label: 'View details',
            onClick: () => handleViewRef.current(row),
          },
        ];

        if (canManage) {
          items.push({
            key: 'generate',
            label: row.pdf_url ? 'Regenerate PDF' : 'Generate deal sheet',
            onClick: () => handleGeneratePdfRef.current(row),
          });
          items.push({
            key: 'supplier-upload',
            label: row.supplier_deal_sheet_url
              ? 'Replace supplier deal sheet'
              : 'Upload supplier deal sheet',
            onClick: () => handleOpenUploadModalRef.current(row),
          });
        }

        if (canManage && canDeleteDealSheet(row)) {
          items.push({
            key: 'delete',
            label: 'Delete',
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: 'Delete this deal sheet?',
                content: 'Only unpaid deal sheets with no payments can be deleted.',
                okText: 'Delete',
                okButtonProps: { danger: true },
                onOk: () => handleDeleteRef.current(row),
              });
            },
          });
        }

        return (
          <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
            {canManage && (
              <Tooltip title={row.pdf_url ? 'Regenerate PDF' : 'Generate deal sheet'}>
                <Button
                  type="text"
                  icon={<FilePdfOutlined />}
                  loading={generatingId === resolveDealSheetId(row)}
                  aria-label={row.pdf_url ? 'Regenerate deal sheet PDF' : 'Generate deal sheet PDF'}
                  onClick={(e) => handleGeneratePdfRef.current(row, e)}
                />
              </Tooltip>
            )}
            {canManage && (
              <Tooltip
                title={
                  row.supplier_deal_sheet_url
                    ? 'Replace supplier deal sheet'
                    : 'Upload supplier deal sheet'
                }
              >
                <Button
                  type="text"
                  icon={<UploadOutlined />}
                  aria-label={
                    row.supplier_deal_sheet_url
                      ? 'Replace supplier deal sheet'
                      : 'Upload supplier deal sheet'
                  }
                  onClick={(e) => handleOpenUploadModalRef.current(row, e)}
                />
              </Tooltip>
            )}
            <Dropdown menu={{ items }} trigger={['click']}>
              <Button
                type="text"
                icon={<MoreOutlined />}
                aria-label="Actions"
              />
            </Dropdown>
          </div>
        );
      },
    };

    return [...getPurchaseDealSheetColumns({ sortState }), actionsColumn];
  }, [sortState, canManage, generatingId]);

  const handleReset = useCallback(() => {
    setSearchInput('');
    setSearch('');
    setPaymentStatus('');
    setTransactionType('');
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, []);

  const hasActiveFilters = Boolean(search || paymentStatus || transactionType);
  const filterDrawerCount = [paymentStatus, transactionType].filter(Boolean).length;

  const filterControls = (
    <div className="flex flex-col gap-5">
      <FilterField label="Payment status">
        <Select
          allowClear
          placeholder="All statuses"
          value={paymentStatus || undefined}
          onChange={(v) => {
            setPaymentStatus(v || '');
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
          options={PAYMENT_STATUS_FILTER_OPTIONS.filter((o) => o.value !== '').map((o) => ({
            value: o.value,
            label: o.label,
          }))}
          className="w-full"
        />
      </FilterField>
      <FilterField label="Transaction type">
        <Select
          allowClear
          placeholder="All types"
          value={transactionType || undefined}
          onChange={(v) => {
            setTransactionType(v || '');
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
          options={TRANSACTION_TYPE_FILTER_OPTIONS.filter((o) => o.value !== '').map((o) => ({
            value: o.value,
            label: o.label,
          }))}
          className="w-full"
        />
      </FilterField>
    </div>
  );

  return (
    <DashboardPageWrapper>
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-white shadow-sm">
        {/* ─── Sticky toolbar ─── */}
        <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-4 py-2.5">
          <PermissionGate permission={PERMISSIONS.PRODUCT_PRE_IPO_PURCHASE_DEAL_SHEET_MANAGE}>
            <Tooltip title="Create deal sheet">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/pre-ipo/purchase-deal-sheet/create')}
                className="aspect-square px-0 sm:aspect-auto sm:px-4"
                aria-label="Create deal sheet"
              >
                <span className="hidden sm:inline">Create</span>
              </Button>
            </Tooltip>
          </PermissionGate>

          {canManage ? <div className="mx-1 h-5 w-px shrink-0 bg-gray-200" /> : null}

          <Input
            allowClear
            prefix={<SearchOutlined className="text-gray-400" />}
            placeholder="Search supplier name or PAN"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="min-w-0 flex-1"
          />

          <Badge count={filterDrawerCount} size="small" offset={[-2, 2]}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFilterDrawerOpen(true)}
              className={`aspect-square px-0 sm:aspect-auto sm:px-4 ${filterDrawerCount > 0 ? 'border-blue-400 text-blue-600' : ''}`}
              aria-label="Filters"
            >
              <span className="hidden sm:inline">Filters</span>
            </Button>
          </Badge>

          <span className="hidden sm:inline-flex">
            <Tooltip title="Refresh">
              <Button
                icon={<FiRefreshCw />}
                onClick={() => fetchListRef.current()}
                loading={loading}
                aria-label="Refresh"
                className="aspect-square min-w-9 px-0"
              />
            </Tooltip>
          </span>
        </div>

        {searchInput.trim().length > 0 && searchInput.trim().length < SUPPLIER_SEARCH_MIN ? (
          <div className="shrink-0 border-b border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-700">
            Type at least {SUPPLIER_SEARCH_MIN} characters to search by supplier name
          </div>
        ) : null}

        {hasActiveFilters ? (
          <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-gray-100 bg-gray-50/60 px-4 py-2">
            {search ? (
              <Tag
                closable
                onClose={() => {
                  setSearchInput('');
                  setSearch('');
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                className="m-0 text-xs"
              >
                Search: <span className="font-medium">{search}</span>
              </Tag>
            ) : null}
            {paymentStatus ? (
              <Tag
                closable
                onClose={() => {
                  setPaymentStatus('');
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                className="m-0 text-xs"
              >
                Payment:{' '}
                <span className="font-medium">
                  {PAYMENT_STATUS_LABELS[paymentStatus] || paymentStatus}
                </span>
              </Tag>
            ) : null}
            {transactionType ? (
              <Tag
                closable
                onClose={() => {
                  setTransactionType('');
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                className="m-0 text-xs"
              >
                Type:{' '}
                <span className="font-medium">
                  {TRANSACTION_TYPE_LABELS[transactionType] || transactionType}
                </span>
              </Tag>
            ) : null}
            <button
              type="button"
              onClick={handleReset}
              className="ml-1 text-xs font-medium text-gray-400 transition-colors hover:text-gray-700"
            >
              Reset all
            </button>
          </div>
        ) : null}

        {error ? (
          <div className="shrink-0 px-4 pt-3">
            <Alert type="error" message={error} showIcon closable onClose={() => setError('')} />
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
          <Table
            rowKey={(r) => r.uuid || r.id}
            columns={columns}
            dataSource={rows}
            loading={loading}
            size="small"
            className="purchase-deal-sheet-table [&_.ant-table-thead>tr>th]:whitespace-nowrap [&_.ant-table-tbody>tr>td]:whitespace-nowrap"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (count, range) => (
                <span className="text-xs text-gray-500">
                  {range[0]}–{range[1]} of {count}
                </span>
              ),
            }}
            onChange={handleTableChange}
            onRow={(row) => ({
              onClick: () => handleView(row),
              className: 'cursor-pointer hover:bg-gray-50',
            })}
          />
        </div>
      </div>

      <Drawer
        title="Filters"
        placement="right"
        width={360}
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              onClick={() => {
                handleReset();
                setFilterDrawerOpen(false);
              }}
            >
              Reset
            </Button>
            <Button type="primary" onClick={() => setFilterDrawerOpen(false)}>
              Apply
            </Button>
          </div>
        }
      >
        {filterControls}
      </Drawer>

      <SupplierDealSheetUploadModal
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setUploadModalRow(null);
        }}
        dealSheetId={resolveDealSheetId(uploadModalRow)}
        supplierDealSheetUrl={uploadModalRow?.supplier_deal_sheet_url}
        onSuccess={handleUploadSuccess}
      />
    </DashboardPageWrapper>
  );
}
